import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { personLabel } from '../../components/ApprovalLevels.js';


interface PendingApproval {
  readonly instanceId: string;
  readonly instanceRefNumber: string | null;
  readonly entityType: string | null;
  readonly entityRefNumber: string | null;
  readonly initiatorName: string | null;
  readonly initiatorLoginId: string | null;
  readonly assignedRoleCode: string | null;
  readonly isOverdue: boolean;
  readonly submittedAt: string | null;
  readonly assignedAt: string | null;
  /** Where this kind of request is decided; the row then only points there. */
  readonly decisionUrl: string | null;
}

interface DecidedApproval {
  readonly instanceId: string;
  readonly instanceRefNumber: string | null;
  readonly entityType: string | null;
  readonly entityRefNumber: string | null;
  readonly instanceStatus: string;
  readonly stepSequence: number;
  readonly stepStatus: string;
  readonly actionedAt: string | null;
}

export function ApprovalInboxPage(): ReactNode {
  const { t } = useTranslation();
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<PendingApproval[] | null>(null);
  const [decided, setDecided] = useState<DecidedApproval[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    apiRequest<{ data: PendingApproval[] }>('/v1/wf/my-pending-approvals', { token })
      .then((r) => { if (alive) setRows(r.data ?? []); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    // A decision must stay visible after it drops out of the pending queue,
    // otherwise an approved request simply vanishes off the approver's screen.
    apiRequest<{ data: DecidedApproval[] }>('/v1/wf/my-recent-decisions', { token })
      .then((r) => { if (alive) setDecided(r.data ?? []); })
      .catch(() => { /* the pending queue is the page; history is a bonus */ });
    return () => { alive = false; };
  }, [token]);
  return (
    <main aria-label="inbox">
      <h1>{t('approvals.inbox.title')}</h1>
      <p style={{ fontSize: '0.875rem', margin: '0 0 1rem' }}>
        <Link to="/inbox/delegate">{t('approvals.inbox.delegate')}</Link>
      </p>
      {error ? <p role="alert">{error}</p> : null}
      {!rows ? <p>{t('approvals.inbox.loading')}</p> : rows.length === 0 ? <p>{t('approvals.inbox.empty')}</p> : (
        <table aria-label="inbox-items" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH}>{t('approvals.inbox.colRef')}</th>
              <th style={TH}>{t('approvals.inbox.colType')}</th>
              <th style={TH}>{t('approvals.inbox.colRaisedBy')}</th>
              <th style={TH}>{t('approvals.inbox.colSubmitted')}</th>
              <th style={TH}>{t('approvals.inbox.colWaitingOn')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.instanceId}>
                <td style={TD}>
                  {/* Owner, 19 Oct: an intimation — a branding request is decided
                      on Branding Review, so the click goes there. */}
                  <Link to={r.decisionUrl ?? `/inbox/${r.instanceId}`}>
                    {r.entityRefNumber ?? r.instanceRefNumber ?? '—'}
                  </Link>
                  {r.isOverdue ? (
                    <span style={{ color: 'var(--color-danger, #c5221f)', marginLeft: 8 }}>
                      {t('approvals.inbox.overdue')}
                    </span>
                  ) : null}
                  {r.instanceRefNumber && r.entityRefNumber ? (
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted, #5f6368)' }}>
                      {r.instanceRefNumber}
                    </div>
                  ) : null}
                </td>
                <td style={TD}>{r.entityType ?? '—'}</td>
                <td style={TD}>{personLabel(r.initiatorName, r.initiatorLoginId) ?? '—'}</td>
                <td style={TD}>{at(r.submittedAt)}</td>
                <td style={TD}>{r.assignedRoleCode ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {decided.length > 0 ? (
        <section aria-label="decided-by-me" style={{ marginTop: '2rem' }}>
          <h2 style={{ fontSize: '1rem' }}>{t('approvals.inbox.decidedTitle')}</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>{t('approvals.inbox.colRef')}</th>
                <th style={TH}>{t('approvals.inbox.colMyDecision')}</th>
                <th style={TH}>{t('approvals.inbox.colDecidedOn')}</th>
                <th style={TH}>{t('approvals.inbox.colRequestNow')}</th>
              </tr>
            </thead>
            <tbody>
              {decided.map((d) => (
                <tr key={`${d.instanceId}-${d.stepSequence}`}>
                  <td style={TD}>
                    <Link to={`/inbox/${d.instanceId}`}>
                      {d.entityRefNumber ?? d.instanceRefNumber ?? '—'}
                    </Link>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted, #5f6368)' }}>
                      {t('approvals.detail.level', { n: d.stepSequence })}
                    </div>
                  </td>
                  <td style={TD}>
                    <span
                      data-testid={`decision-${d.instanceId}`}
                      style={decisionStyle(d.stepStatus)}
                    >
                      {d.stepStatus}
                    </span>
                  </td>
                  <td style={TD}>{at(d.actionedAt)}</td>
                  <td style={TD}>{d.instanceStatus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </main>
  );
}

function decisionStyle(stepStatus: string): CSSProperties {
  const approved = stepStatus === 'APPROVED';
  const colour = approved ? 'var(--color-success, #137333)' : 'var(--color-danger, #c5221f)';
  return {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: 999,
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--color-text-inverse)',
    background: colour,
  };
}

const TH: CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem',
  borderBottom: '2px solid var(--color-border, #dadce0)',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const TD: CSSProperties = {
  padding: '0.5rem',
  borderBottom: '1px solid var(--color-border, #dadce0)',
  verticalAlign: 'top',
};
